import React, { useMemo, useState } from 'react';

const chapters = [
  {
    id: 'frontend',
    label: 'Frontend',
    accent: '#4FC3F7',
    subtitle: 'Interface craft and motion systems',
    narrative:
      'The front-end layer is where I shape the first impression: responsive layout, motion, and the small interactions that make a product feel deliberate.',
    skills: ['React', 'Next.js', 'Angular', 'TypeScript', 'Three.js', 'GSAP', 'Tailwind CSS', 'HTML / CSS'],
  },
  {
    id: 'backend',
    label: 'Backend',
    accent: '#81C784',
    subtitle: 'Services, APIs, and the logic layer',
    narrative:
      'This chapter covers the systems behind the UI: the APIs, services, and orchestration layers that keep the product reliable under real usage.',
    skills: ['Node.js', 'Java Spring Boot', 'Python FastAPI', 'Flask', 'Express', 'WebSockets', 'Kafka', 'REST APIs'],
  },
  {
    id: 'ml',
    label: 'ML / Deep Learning',
    accent: '#E85D34',
    subtitle: 'Modeling, research, and vision work',
    narrative:
      'This is the part of my work where I experiment with learning systems, study failure modes, and move from raw data to usable insight.',
    skills: ['PyTorch', 'CNN', 'ResNet', 'Vision Transformers', 'LoRA', 'DeBERTa', 'DistilBERT', 'RoBERTa', 'LSTM', 'FGSM / PGD', 'GradCAM'],
  },
  {
    id: 'genai',
    label: 'Agentic AI / GenAI',
    accent: '#CE93D8',
    subtitle: 'Retrieval, prompts, and tool use',
    narrative:
      'I use this layer when a product needs orchestration, not just answers but retrieval pipelines, prompt systems, and tool-using agents.',
    skills: ['LangChain', 'RAG Pipelines', 'Zephyr / LLMs', 'Prompt Engineering', 'ChatBot Systems', 'Tool-Use Agents'],
  },
  {
    id: 'cv',
    label: 'Computer Vision',
    accent: '#FF8A65',
    subtitle: 'Image understanding and detection',
    narrative:
      'This track reflects the visual side of the work: detection, segmentation, and the practical problems that show up when pixels need to mean something.',
    skills: ['SAM2', 'YOLO', 'ImageNet', 'Torchvision', 'Adversarial Attacks', 'Bioacoustic Monitoring', 'LiDAR'],
  },
  {
    id: 'infra',
    label: 'Systems / Infra',
    accent: '#4DD0E1',
    subtitle: 'Deployment, reliability, and automation',
    narrative:
      'When a project needs to ship and stay healthy, this is the layer I lean on: containers, automation, and the infrastructure that keeps the work repeatable.',
    skills: ['Docker', 'Terraform', 'Dagster', 'GitHub Actions', 'CI/CD', 'MLOps', 'Microservices', 'Scala Migration'],
  },
  {
    id: 'data',
    label: 'Data / Storage',
    accent: '#FFD54F',
    subtitle: 'Persistence, analysis, and query design',
    narrative:
      'This chapter is about storing, moving, and shaping data so systems can depend on it, from relational queries to analysis workflows.',
    skills: ['PostgreSQL', 'MongoDB', 'MySQL', 'Redis', 'NumPy', 'Pandas', 'Matplotlib'],
  },
  {
    id: 'mobile',
    label: 'Mobile / Cross-Platform',
    accent: '#AED581',
    subtitle: 'Apps that travel with the user',
    narrative:
      'When the interface has to leave the desktop, this is the stack I use to keep it consistent across devices and platforms.',
    skills: ['React Native', 'Ionic', 'Angular Mobile', 'iOS / Android'],
  },
  {
    id: 'collab',
    label: 'Collaboration / Real-Time',
    accent: '#F48FB1',
    subtitle: 'Shared state and live interaction',
    narrative:
      'This part of the story is about live systems, synchronized state, fast feedback, and the tools that make collaboration feel immediate.',
    skills: ['WebSockets', 'P2P / YJS', 'Monaco Editor', 'QuillJS', 'OAuth', 'Encryption'],
  },
];

const SkillsJourney = () => {
  const [activeId, setActiveId] = useState(chapters[0].id);
  const [hoveredId, setHoveredId] = useState(null);

  const totalSkills = useMemo(() => chapters.reduce((sum, chapter) => sum + chapter.skills.length, 0), []);
  const activeChapter = chapters.find((chapter) => chapter.id === activeId) || chapters[0];
  const activeIndex = Math.max(chapters.findIndex((chapter) => chapter.id === activeChapter.id), 0);

  return (
    <div className="skills-journey-shell">
      <div className="skills-journey-header">
        <div>
          <p className="skills-journey-kicker">Skills</p>
          <h2 className="skills-journey-title">A full field atlas, not a tag cloud.</h2>
          <p className="skills-journey-copy">
            Every discipline is still here, the whole catalog, organized by how I actually build across interface, systems, research, and deployment.
          </p>
        </div>
        <div className="skills-journey-stats">
          <div className="skills-journey-stat">
            <span className="skills-journey-stat-value">{chapters.length}</span>
            <span className="skills-journey-stat-label">disciplines</span>
          </div>
          <div className="skills-journey-stat">
            <span className="skills-journey-stat-value">{totalSkills}</span>
            <span className="skills-journey-stat-label">skills</span>
          </div>
        </div>
      </div>

      <div className="skills-journey-stage">
        <div className="skills-journey-track-wrapper">
          <div className="skills-journey-track" aria-hidden="true">
            <span
              className="skills-journey-track-fill"
              style={{ height: `${((activeIndex + 1) / chapters.length) * 100}%` }}
            />
          </div>

          <div className="skills-journey-rail">
            {chapters.map((chapter, index) => {
              const isActive = chapter.id === activeId;
              const isHovered = chapter.id === hoveredId;
              return (
                <button
                  key={chapter.id}
                  type="button"
                  className={`skills-journey-node${isActive ? ' active' : ''}${isHovered ? ' hovered' : ''}`}
                  onClick={() => setActiveId(chapter.id)}
                  onMouseEnter={() => setHoveredId(chapter.id)}
                  onMouseLeave={() => setHoveredId(null)}
                  style={{ '--journey-accent': chapter.accent }}
                >
                  <span className="skills-journey-node-index">0{index + 1}</span>
                  <span className="skills-journey-node-dot" />
                  <span className="skills-journey-node-copy">
                    <span className="skills-journey-node-label">{chapter.label}</span>
                    <span className="skills-journey-node-subtitle">{chapter.subtitle}</span>
                  </span>
                  <span className="skills-journey-node-count">{chapter.skills.length}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="skills-journey-detail" style={{ '--journey-accent': activeChapter.accent }}>
          <div className="skills-journey-detail-top">
            <div>
              <p className="skills-journey-detail-label">{activeChapter.label}</p>
              <h3 className="skills-journey-detail-title">{activeChapter.subtitle}</h3>
            </div>
            <span className="skills-journey-detail-badge">{activeChapter.skills.length} skills</span>
          </div>

          <p className="skills-journey-detail-text">{activeChapter.narrative}</p>

          <div className="skills-journey-skill-grid">
            {activeChapter.skills.map((skill, index) => (
              <button
                key={skill}
                type="button"
                className={`skills-journey-chip${index % 3 === 0 ? ' tall' : ''}${index % 4 === 0 ? ' bright' : ''}`}
                onClick={() => setActiveId(activeChapter.id)}
              >
                <span className="skills-journey-chip-index">{String(index + 1).padStart(2, '0')}</span>
                <span className="skills-journey-chip-text">{skill}</span>
              </button>
            ))}
          </div>

          <div className="skills-journey-footer">
            <span>Hover a discipline to preview</span>
            <span>Click to lock the story arc</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SkillsJourney;
